<template>
  <div :class="twMerge(wrapperVariants({ theme }), checked && wrapperActiveVariants({ theme }))">
    <div class="checkbox-wrapper" :class="`checkbox-${theme}`">
      <input
        :id="label"
        v-model="checked"
        type="checkbox"
        :required="required"
        class="cursor-pointer"
      />
      <span class="checkbox cursor-pointer"></span>
    </div>

    <div class="ml-5 flex cursor-pointer flex-col transition-colors duration-500">
      <label
        :for="label"
        :class="twMerge('cursor-pointer', checked ? 'font-bold !text-white' : 'text-black')"
      >
        {{ label }}
      </label>
      <span
        v-if="description"
        class="text-[10px] leading-[15px]"
        :class="[checked ? 'text-white' : '']"
      >
        {{ description }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { cva } from 'class-variance-authority'
import { THEMES } from '../../types/themes'
import { twMerge } from 'tailwind-merge'

const props = withDefaults(
  defineProps<{
    label: string
    modelValue: boolean
    description?: string
    required?: boolean
    theme?: THEMES
  }>(),
  {
    theme: THEMES.DEFAULT,
  },
)

const emit = defineEmits(['update:modelValue'])

const checked = useVModel(props, 'modelValue', emit)

const wrapperVariants = cva(
  'flex max-w-sm cursor-pointer rounded-md px-6 py-[14px]  transition-colors duration-500 bg-white',
  {
    variants: {
      theme: {
        [THEMES.DEFAULT]: 'shadow-[0_4px_30px_0px_rgba(0,180,210,0.2)]',
        [THEMES.RDK]: 'shadow-[0_4px_30px_0_rgba(122,121,174,0.25)]',
        [THEMES.DJURENS_RATT]: '',
      },
    },
    defaultVariants: {
      theme: THEMES.DEFAULT,
    },
  },
)

const wrapperActiveVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'bg-blue',
      [THEMES.RDK]: ' bg-rdk-orange',
      [THEMES.DJURENS_RATT]: '',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})
</script>

<style scoped>
.checkbox-default.checkbox-wrapper {
  --borderColor: #00b4d2;
  --borderWidth: 2px;
}

.checkbox-rdk.checkbox-wrapper {
  --borderColor: #7a54c3;
  --borderWidth: 2px;
}

.checkbox-wrapper input[type='checkbox'] {
  -webkit-appearance: none;
  appearance: none;
  vertical-align: middle;
  background: #fff;
  border-radius: 5px;
  display: inline-block;
  border: var(--borderWidth) solid var(--borderColor);
  width: 27px;
  height: 27px;
  position: relative;
}

.checkbox-wrapper input[type='checkbox']:checked {
  border: white;
}

.checkbox-wrapper input[type='checkbox']:after {
  content: '';
  position: absolute;
  background: var(--borderColor);
  width: 9px;
  height: 3px;
  top: 55%;
  left: 30%;
  transform-origin: left center;
  border-radius: 4px;
}

.checkbox-wrapper input[type='checkbox']:checked:after {
  width: calc(var(--borderWidth) * 8);
  transform: rotate(-45deg) translateY(calc(var(--borderWidth) * 2)) scaleX(1);
  transition: transform 200ms ease-out 200ms;
}

.checkbox-wrapper input[type='checkbox']:before {
  content: '';
  position: absolute;
  background: var(--borderColor);
  width: 10px;
  height: 3px;
  top: 45%;
  left: 20%;
  transform-origin: left center;
  border-radius: 4px;
}
.checkbox-wrapper input[type='checkbox']:checked:before {
  transform: rotate(45deg) translate(calc(var(--borderWidth) / -2), calc(var(--borderWidth) / -2))
    scaleX(1);
  transition: transform 200ms ease-in;
}

.checkbox-wrapper input[type='checkbox']:before {
  transform: rotate(45deg) translate(calc(var(--borderWidth) / -2), calc(var(--borderWidth) / -2))
    scaleX(0);
  transition: transform 200ms ease-in 200ms;
}
.checkbox-wrapper input[type='checkbox']:after {
  width: calc(var(--borderWidth) * 5);
  transform: rotate(-45deg) translateY(calc(var(--borderWidth) * 2)) scaleX(0);
  transform-origin: left center;
  transition: transform 200ms ease-in;
}

.checkbox-wrapper input[type='checkbox']:focus {
  outline: none !important;
  box-shadow: none !important;
}
</style>
